import React from 'react';

import { ArrowRightOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { trackEvent } from '@app/services/analytics.service';
import { StyledResourceCard } from '@components/shared/ResourceCard/ResourceCard.style';
import { EDiloLimitStatus } from '@FW/features/eDilo/api/eDilo.dto.types';
import { useEDiloLimits } from '@FW/features/eDilo/api/eDilo.queries';
import { formatNumberWithDecimals } from '@utils/shared/number/formatNumberWithDecimals';
import { formatReadableNumber } from '@utils/shared/number/formatReadableNumber';
import { Button, Divider, Flex, Skeleton, Tooltip, Typography } from 'antd';
import clsx from 'clsx';
import { t } from 'i18next';
import { Link } from 'react-router-dom';

const { Text } = Typography;

const EDILO_CABINET_URL = 'https://web.edilo.com.ua';

const TITLE_KEY_BY_STATUS: Record<EDiloLimitStatus, string> = {
	prelimit: 'eDilo.titlePrelimit',
	approved: 'eDilo.titleAvailable',
	rejected: 'eDilo.titleRejected',
};

const EDiloBalanceOverview: React.FC = () => {
	const { data, isPending, isError } = useEDiloLimits();

	/**
	 * This card sits on the home page for every client, so mounting it is not a
	 * signal (mobile's event fires on a dedicated screen the user navigated to).
	 * The web analogue of opening that screen is following one of the card's two
	 * eDilo entry points.
	 */
	const trackOpen = () => trackEvent('edilo_overview_viewed', {});

	if (isError) return null;

	const titleKey = data?.status ? TITLE_KEY_BY_STATUS[data.status] : 'eDilo.title';

	return (
		<StyledResourceCard vertical gap={8} className={clsx('card w-full')}>
			<Flex gap={8} align='center' justify='space-between'>
				<Flex gap={8} align='center'>
					<Text type='secondary' className='title'>
						{t(titleKey)}
					</Text>
					<Tooltip title={t('eDilo.description')}>
						<InfoCircleOutlined className='text-gray' />
					</Tooltip>
				</Flex>
				<Link
					to='/money-deposit?tab=invoices&action=create'
					aria-label={t('eDilo.createCTA')}
					onClick={trackOpen}>
					<ArrowRightOutlined className='text-green' />
				</Link>
			</Flex>

			<Divider style={{ margin: 0 }} />

			{isPending ? (
				<Skeleton.Button style={{ height: 56 }} className='block--i w-full--i' active />
			) : data.status === 'rejected' ? (
				<Text type='secondary'>{t('eDilo.rejectedMessage')}</Text>
			) : data.status === 'prelimit' ? (
				// Nothing is drawn down yet, so available/used are both 0 — the approved total is
				// the only number that means anything in this state.
				<Flex justify='space-between' align='center' gap={16} className='nomenclature-wrapper'>
					<Text>{t('eDilo.preapprovedLimit')}</Text>
					<Text style={{ color: 'green', whiteSpace: 'nowrap' }}>
						{formatReadableNumber(formatNumberWithDecimals(data.total))}
					</Text>
				</Flex>
			) : (
				<Flex vertical gap={8}>
					<Flex justify='space-between' align='center' gap={16} className='nomenclature-wrapper'>
						<Text>{t('eDilo.availableBalance')}</Text>
						<Text style={{ color: 'green' }}>
							{formatReadableNumber(formatNumberWithDecimals(data.available))}
						</Text>
					</Flex>
					<Flex justify='space-between' align='center' gap={16} className='nomenclature-wrapper'>
						<Text>{t('eDilo.usedAmount')}</Text>
						<Text style={{ color: 'red' }}>
							{formatReadableNumber(formatNumberWithDecimals(data.used))}
						</Text>
					</Flex>
				</Flex>
			)}

			<Button
				href={EDILO_CABINET_URL}
				target='_blank'
				rel='noopener noreferrer'
				onClick={trackOpen}
				block
				type='primary'
				className='mt-auto'
				style={{ background: '#313434', color: '#a9f9f2' }}>
				{t('eDilo.cabinetLink')}
			</Button>
		</StyledResourceCard>
	);
};

export default EDiloBalanceOverview;
